import axios from "axios";
import { API_URL } from "./endpoints";

// Create axios instance with base configuration
const api = axios.create({
  baseURL: `${API_URL}/api`, // Force backend URL
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Include cookies in requests
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Get token from localStorage
    const token = localStorage.getItem("authToken");

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add request timestamp for debugging
    config.metadata = { startTime: new Date() };

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and token refresh
api.interceptors.response.use(
  (response) => {
    // Calculate request duration
    if (response.config.metadata) {
      const duration = new Date() - response.config.metadata.startTime;
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 errors (unauthorized)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Clear stored token and redirect to login
      localStorage.removeItem("authToken");
      localStorage.removeItem("user");

      // Only redirect if not already on login page
      if (!["/login", "/reset-password"].includes(window.location.pathname)) {
        window.location.href = "/login";
      }
    }

    // Handle network errors
    if (!error.response) {
      console.error("Network Error:", error.message);
      error.message = "Network error. Please check your connection.";
    }

    // Handle server errors
    if (error.response?.status >= 500) {
      console.error("Server Error:", error.response.data);
      error.message = "Server error. Please try again later.";
    }

    return Promise.reject(error);
  }
);

// API methods
export const authAPI = {
  login: (credentials) => api.post("/auth/login", credentials),
  register: (userData) => api.post("/auth/register", userData),
  forgotPassword: (email) => api.post("/auth/forgot-password", { email }),
  resetPassword: (token, newPassword) =>
    api.post("/auth/reset-password", { token, newPassword }),
  verifyToken: (token) => api.post("/auth/verify-token", { token }),
  refreshToken: () => api.post("/auth/refresh-token"),
  logout: () => api.post("/auth/logout"),
};

export const userAPI = {
  getUsers: () => api.get("/users"),
  getUser: (id) => api.get(`/users/${id}`),
  createUser: (userData) => api.post("/users", userData),
  updateUser: (id, userData) => api.put(`/users/${id}`, userData),
  deleteUser: (id) => api.delete(`/users/${id}`),
  activateUser: (id) => api.put(`/users/${id}/activate`),
  resetUserPassword: (id, newPassword) =>
    api.post(`/users/${id}/reset-password`, { newPassword }),
  updateProfile: (userData) => api.put("/users/profile", userData),

  // Pinned projects (starred boards)
  getPinned: () => api.get("/users/me/pinned"),
  setPinned: (pinned) => api.put("/users/me/pinned", { pinned }),

  // Recently viewed
  getRecentlyViewed: () => api.get("/users/me/recently-viewed"),
  recordRecentlyViewed: (projectId) => api.post("/users/me/recently-viewed", { projectId }),

  // Bookmarks
  getBookmarks: () => api.get("/users/me/bookmarks"),
  addBookmark: (cardId) => api.post(`/users/me/bookmarks/${cardId}`),
  removeBookmark: (cardId) => api.delete(`/users/me/bookmarks/${cardId}`),
};

export const projectAPI = {
  getProjects: () => api.get("/projects"),
  getProject: (id) => api.get(`/projects/${id}`),
  createProject: (projectData) => api.post("/projects", projectData),
  updateProject: (id, projectData) => api.put(`/projects/${id}`, projectData),
  deleteProject: (id) => api.delete(`/projects/${id}`),
  // Archive functionality
  getArchivedProjects: () => api.get("/projects/archived"),
  restoreProject: (id) => api.put(`/projects/${id}/restore`),
  permanentDeleteProject: (id) => api.delete(`/projects/${id}/permanent`),
  addMember: (id, memberData) =>
    api.post(`/projects/${id}/members`, memberData),
  removeMember: (id, userId) => api.delete(`/projects/${id}/members/${userId}`),
  getProjectLabels: (id) => api.get(`/projects/${id}/labels`),
  createLabel: (id, labelData) => api.post(`/projects/${id}/labels`, labelData),
  updateLabel: (id, labelId, labelData) =>
    api.put(`/projects/${id}/labels/${labelId}`, labelData),
  deleteLabel: (id, labelId) => api.delete(`/projects/${id}/labels/${labelId}`),

  // Project file upload and attachment management
  uploadFiles: (projectId, formData) => {
    const uploadApi = axios.create({
      baseURL: `${API_URL}/api`,
      timeout: 30000, // 30 seconds for file uploads
      withCredentials: true,
    });

    // Add auth token
    const token = localStorage.getItem("authToken");
    if (token) {
      uploadApi.defaults.headers.Authorization = `Bearer ${token}`;
    }

    return uploadApi.post(`/projects/${projectId}/upload`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  deleteAttachment: (projectId, attachmentId) => {
    const deleteApi = axios.create({
      baseURL: `${API_URL}/api`,
      timeout: 10000,
      withCredentials: true,
    });

    // Add auth token
    const token = localStorage.getItem("authToken");
    if (token) {
      deleteApi.defaults.headers.Authorization = `Bearer ${token}`;
    }

    return deleteApi.delete(
      `/projects/${projectId}/attachments/${attachmentId}`
    );
  },

  // Credentials API (admin manages, specific members can view)
  getPublicKey: () => api.get("/projects/encryption/public-key"),
  addCredential: (projectId, credentialData) =>
    api.post(`/projects/${projectId}/credentials`, credentialData),
  updateCredential: (projectId, credentialId, credentialData) =>
    api.put(`/projects/${projectId}/credentials/${credentialId}`, credentialData),
  deleteCredential: (projectId, credentialId) =>
    api.delete(`/projects/${projectId}/credentials/${credentialId}`),
  revealCredential: (projectId, credentialId) =>
    api.post(`/projects/${projectId}/credentials/${credentialId}/reveal`),

  // Credential Access Management (admin only)
  grantCredentialAccess: (projectId, memberId) =>
    api.post(`/projects/${projectId}/credential-access/${memberId}`),
  revokeCredentialAccess: (projectId, memberId) =>
    api.delete(`/projects/${projectId}/credential-access/${memberId}`),

  // Descriptions API (admin can add/edit/delete, members can view)
  addDescription: (projectId, descriptionData) =>
    api.post(`/projects/${projectId}/descriptions`, descriptionData),
  updateDescription: (projectId, descriptionId, descriptionData) =>
    api.put(`/projects/${projectId}/descriptions/${descriptionId}`, descriptionData),
  deleteDescription: (projectId, descriptionId) =>
    api.delete(`/projects/${projectId}/descriptions/${descriptionId}`),
  revealDescription: (projectId, descriptionId) =>
    api.post(`/projects/${projectId}/descriptions/${descriptionId}/reveal`),
};

// Category API
export const categoryAPI = {
  getCategories: () => api.get("/categories"),
  getCategoriesWithCounts: () => api.get("/categories/with-counts"),
  getCategory: (id) => api.get(`/categories/${id}`),
  createCategory: (categoryData) => api.post("/categories", categoryData),
  updateCategory: (id, categoryData) => api.put(`/categories/${id}`, categoryData),
  deleteCategory: (id) => api.delete(`/categories/${id}`),
  getProjectsByCategory: (id) => api.get(`/categories/${id}/projects`),
};

export const cardAPI = {
  getCards: (projectId, includeArchived = false) => {
    const params = includeArchived ? "?includeArchived=true" : "";
    return api.get(`/projects/${projectId}/cards${params}`);
  },
  searchCards: (projectId, q, limit = 20, config = {}) =>
    api.get(
      `/projects/${projectId}/cards/search?q=${encodeURIComponent(
        q
      )}&limit=${limit}`,
      config
    ),
  getCard: (id) => api.get(`/cards/${id}`),
  getCardsDueToday: () => api.get("/cards/due-today"),
  getCardsBackDate: () => api.get("/cards/back-date"),
  getCardsUpcoming: () => api.get("/cards/upcoming"),
  createCard: (cardData) => api.post("/cards", cardData),
  updateCard: (id, cardData) => api.put(`/cards/${id}`, cardData),
  archiveCard: (id) => api.put(`/cards/${id}/archive`),
  restoreCard: (id) => api.put(`/cards/${id}/restore`),
  deleteCard: (id) => api.delete(`/cards/${id}`),
  markAsRead: (id) => api.put(`/cards/${id}/read`),
  updateStatus: (id, status) => api.put(`/cards/${id}/status`, { status }),
  updateProject: (id, projectId) => api.put(`/cards/${id}/project`, { projectId }),
  reorderCards: (cardOrders) => api.put(`/cards/reorder`, { cardOrders }),
  toggleComplete: (id) => api.put(`/cards/${id}/complete`),
  moveAllCards: (projectId, sourceStatus, targetStatus) =>
    api.post(`/projects/${projectId}/cards/move-all`, {
      sourceStatus,
      targetStatus,
    }),
  assignUser: (id, userId) => api.post(`/cards/${id}/assign`, { userId }),
  unassignUser: (id, userId) => api.delete(`/cards/${id}/assign/${userId}`),
  addComment: (id, comment, mentions = [], parentId = null) =>
    api.post(`/cards/${id}/comments`, { comment, mentions, parentId }),
  updateComment: (id, commentId, text) =>
    api.put(`/cards/${id}/comments/${commentId}`, { text }),
  deleteComment: (id, commentId) =>
    api.delete(`/cards/${id}/comments/${commentId}`),
  addLabel: (id, labelData) => api.post(`/cards/${id}/labels`, labelData),
  removeLabel: (id, labelId) => api.delete(`/cards/${id}/labels/${labelId}`),
  addAttachment: (id, attachmentData) =>
    api.post(`/cards/${id}/attachments`, attachmentData),
  deleteAttachment: (id, attachmentId) =>
    api.delete(`/cards/${id}/attachments/${attachmentId}`),
  uploadFiles: (id, formData) => {
    const uploadApi = axios.create({
      baseURL: `${API_URL}/api`,
      timeout: 30000, // 30 seconds for file uploads
      withCredentials: true,
    });

    // Add auth token
    const token = localStorage.getItem("authToken");
    if (token) {
      uploadApi.defaults.headers.Authorization = `Bearer ${token}`;
    }

    return uploadApi.post(`/cards/${id}/upload-files`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },
  addRelatedCard: (id, relatedCardId) =>
    api.post(`/cards/${id}/related`, { relatedCardId }),
  removeRelatedCard: (id, relatedCardId) =>
    api.delete(`/cards/${id}/related/${relatedCardId}`),
  searchProjectCards: (projectId, query) =>
    api.get(`/projects/${projectId}/cards/search`, { params: { q: query } }),
};

export const cardItemAPI = {
  getCardItems: (cardId) => api.get(`/card-items/cards/${cardId}/items`),
  createCardItem: (cardId, itemData) =>
    api.post(`/card-items/cards/${cardId}/items`, itemData),
  updateCardItem: (cardId, itemId, itemData) =>
    api.put(`/card-items/cards/${cardId}/items/${itemId}`, itemData),
  deleteCardItem: (cardId, itemId) =>
    api.delete(`/card-items/cards/${cardId}/items/${itemId}`),
  reorderCardItems: (cardId, items) =>
    api.put(`/card-items/cards/${cardId}/items/reorder`, { items }),
};

export const columnAPI = {
  getColumns: (projectId) => api.get(`/columns/projects/${projectId}/columns`),
  createColumn: (projectId, columnData) =>
    api.post(`/columns/projects/${projectId}/columns`, columnData),
  updateColumn: (projectId, columnId, columnData) =>
    api.put(`/columns/projects/${projectId}/columns/${columnId}`, columnData),
  deleteColumn: (projectId, columnId) =>
    api.delete(`/columns/projects/${projectId}/columns/${columnId}`),
  reorderColumns: (projectId, columns) =>
    api.put(`/columns/projects/${projectId}/columns/reorder`, { columns }),
};

export const notificationAPI = {
  getNotifications: (params) => api.get("/notifications", { params }),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllAsRead: () => api.put("/notifications/mark-all-read"),
  deleteNotification: (id) => api.delete(`/notifications/${id}`),
  createNotification: (notificationData) =>
    api.post("/notifications", notificationData),
};

export const activityAPI = {
  getProjectActivities: (projectId, page = 1, limit = 50) =>
    api.get(`/activities/project/${projectId}?page=${page}&limit=${limit}`),
  getUserActivities: (userId, page = 1, limit = 50) =>
    api.get(`/activities/user/${userId}?page=${page}&limit=${limit}`),
  getRecentActivities: (limit = 20) =>
    api.get(`/activities/recent?limit=${limit}`),
};

export const timeEntryAPI = {
  // Timer operations
  startTimer: (cardId) => api.post("/time-entries/timer/start", { cardId }),
  stopTimer: () => api.post("/time-entries/timer/stop"),
  getActiveTimer: () => api.get("/time-entries/timer/active"),
  discardTimer: () => api.delete("/time-entries/timer/discard"),
  getAllActiveTimers: () => api.get("/time-entries/timers/all"),
  getRunningTasks: () => api.get("/time-entries/running-tasks"),
  getProjectActiveTimers: (projectId) =>
    api.get(`/time-entries/projects/${projectId}/active-timers`),

  // Manual time entries
  addEntry: (data) => api.post("/time-entries", data),
  updateEntry: (id, data) => api.put(`/time-entries/${id}`, data),
  deleteEntry: (id) => api.delete(`/time-entries/${id}`),

  // Queries
  getCardEntries: (cardId, page = 1) =>
    api.get(`/time-entries/cards/${cardId}?page=${page}`),
  getProjectSummary: (projectId) =>
    api.get(`/time-entries/projects/${projectId}/summary`),
  getProjectEntries: (projectId, filters = {}) => {
    const params = new URLSearchParams();
    if (filters.page) params.append("page", filters.page);
    if (filters.limit) params.append("limit", filters.limit);
    if (filters.startDate) params.append("startDate", filters.startDate);
    if (filters.endDate) params.append("endDate", filters.endDate);
    if (filters.userIds?.length) params.append("userIds", filters.userIds.join(","));
    if (filters.cardId) params.append("cardId", filters.cardId);
    if (filters.labelNames?.length) params.append("labelNames", filters.labelNames.join(","));
    if (filters.entryType) params.append("entryType", filters.entryType);
    if (filters.sortBy) params.append("sortBy", filters.sortBy);
    if (filters.sortOrder) params.append("sortOrder", filters.sortOrder);
    if (filters.summaryOnly !== undefined)
      params.append("summaryOnly", String(filters.summaryOnly));
    return api.get(`/time-entries/projects/${projectId}/entries?${params.toString()}`);
  },

  // Estimated time
  setEstimatedTime: (cardId, estimatedTime) =>
    api.put(`/time-entries/cards/${cardId}/estimated`, { estimatedTime }),
};

// Create upload API instance with longer timeout
const uploadApi = axios.create({
  baseURL: `${API_URL}/api`,
  timeout: 120000, // 2 minutes for AI processing
  withCredentials: true,
});

// Add token to upload API as well
uploadApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// AI API methods
export const aiAPI = {
  // Extract tickets from PDF
  extractTicketsFromPDF: (projectId, formData) =>
    uploadApi.post(`/ai/extract-tickets/${projectId}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      timeout: 120000, // 2 minutes for AI processing
    }),

  // Extract tickets from text
  extractTicketsFromText: (projectId, data) =>
    api.post(`/ai/extract-tickets-from-text/${projectId}`, data, {
      timeout: 120000, // 2 minutes for AI processing
    }),

  // Create cards from reviewed tickets
  createCardsFromTickets: (projectId, ticketRefs) =>
    api.post(`/ai/create-cards/${projectId}`, { ticketRefs }),

  // Get extraction history
  getExtractionHistory: (projectId) =>
    api.get(`/ai/extractions/${projectId}`),

  // Transform description with AI (developer or summary mode)
  transformDescription: (description, mode) =>
    api.post("/ai/transform-description", { description, mode }, {
      timeout: 60000, // 60 seconds for AI processing
    }),
};

export const contactAPI = {
  submitForm: (formData) => api.post("/contacts", formData),
};

export default api;

